import React, {useEffect, useState} from "react";
import { useParams } from "react-router";
import Navbar from "../Navbar/Navbar";
import SideMenu from "./SideMenu";
import { useQuery } from "@tanstack/react-query";
import LoadingElement from "../Loading/LoadingElement";

import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { fetchShippingDetails, fetchShippingWithProductDetails } from "@/api/ShippingsAPI";
import { ArticleModel } from "@/api/Models/ArticleModel";
import { ShippingDetailModel } from "@/api/Models/ShippingDetailModel";
import { toast } from "../ui/use-toast";

function MyShippingDetails() {
    const queryParams = useParams();
    const shippingId = queryParams.shippingId;
    const [currentPage, setCurrentPage] = useState(0);
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const pageSize = 25;

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (dataProducts?.totalPages && value > dataProducts.totalPages) {
            value = dataProducts.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (dataProducts?.totalPages && value >= 1 && value <= dataProducts?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };


    const handlePageChange = (selectedPage: number) => {
        setCurrentPage(selectedPage);
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ["shipping", shippingId],
        queryFn: fetchShippingDetails,
    });
    
    const { data: dataProducts, isLoading: isLoadingProducts, error: errorProducts } = useQuery({
        queryKey: ["shippingDetails", shippingId, currentPage + 1, pageSize],
        queryFn: fetchShippingWithProductDetails,
    });

    if (isLoading) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement /></p>;
    if (shippingId === undefined) return <p>A aparut o eroare la extragerea datelor despre livrare.</p>;

    // if (isLoadingOrder) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement /></p>;
    if (data?.orderId === undefined) return <p>A aparut o eroare la extragerea datelor despre comenzi.</p>;

    if (isLoadingProducts) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement /></p>;

    if (error || errorProducts || !dataProducts) {
        toast({ description: "Exista o eroare la preluarea informatiilor despre aceasta precomanda." });
        return <></>;
    }

    return (
        <>
            <Navbar />
            <div className="flex flex-row items-start">
                <SideMenu />
                {/* Desktop Version */}
                <div className="flex-col flex-grow hidden md:block">
                    <div className="flow-root rounded-lg py-4 ml-8 mt-8 mb-8">
                        <dl className="flex flex-row flex-nowrap gap-3 -my-3 text-sm">
                            <div className="flex flex-col border w-1/3 border-gray-100">
                                <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
                                    <dt className="font-medium underline text-gray-900 ml-2 mr-12">Numar livrare</dt>
                                    <dd className="text-gray-700 sm:col-span-2">{shippingId}</dd>
                                </div>

                                <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
                                    {/* <dt className="font-medium underline text-gray-900 ml-2">Comanda</dt>
                                    <dd className="text-gray-700 sm:col-span-2">
                                    </dd> */}
                                    <dt className="font-medium text-gray-900 ml-2">Numarul comenzii</dt>
                                    <dd className="text-gray-700 sm:col-span-2">
                                        {data?.orderId}
                                    </dd>
                                    <dt className="font-medium text-gray-900 ml-2">Data integrarii comenzii</dt>
                                    <dd className="text-gray-700 sm:col-span-2">
                                        {data?.orderIntegrationDate ? data?.orderIntegrationDate.split("T")[0] : '-'}
                                    </dd>
                                    <dt className="font-medium text-gray-900 ml-2">Data livrarii</dt>
                                    <dd className="text-gray-700 sm:col-span-2">
                                        {data?.deliveryDate ? data?.deliveryDate.split("T")[0] : '-'}
                                    </dd>
                                </div>

                            </div>
                        </dl>
                    </div>

                    <div className="flex-initial w-1/4 overflow-x-auto ml-8 mb-2 shadow-md sm:w-4/5 sm:rounded-lg">

                        <section>
                            <div className="max-w-[1320px] m-auto px-4 mt-1 py-0 sm:px-6 sm:py-2 lg:px-8">
                                <Table>
                                    <TableCaption></TableCaption>
                                    <TableHeader>
                                        <TableRow className="bg-white border-slate-200 mb-1">
                                            <TableHead className="text-nowrap">Cod articol</TableHead>
                                            <TableHead className="sticky left-0 bg-white z-10 w-[100px] text-nowrap">Denumire articol</TableHead>
                                            <TableHead className="text-center">Unitate de vanzare</TableHead>
                                            {/* <TableHead className="text-center text-nowrap">Pret unitar/PCB/BAX <div className="text-blue-900">fara TVA</div></TableHead> */}
                                            <TableHead className="text-center text-nowrap">Pret pe bucata <div className="text-blue-900">fara TVA</div></TableHead>
                                            {/* <TableHead className="text-center text-nowrap">Pret/buc <div className="text-blue-900">TVA inclus</div></TableHead> */}
                                            <TableHead className="text-center">Cantitate comandata</TableHead>
                                            <TableHead className="text-center">Cantitate livrata</TableHead>
                                            <TableHead className="text-center">Total <div className="text-blue-900">fara TVA</div></TableHead>
                                            <TableHead className="text-center">Total <div className="text-blue-900">TVA inclus</div></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {dataProducts?.items.map((orderDetail: ShippingDetailModel, index: number) => {
                                            let product = orderDetail.article as ArticleModel;

                                            return (
                                                <TableRow key={product.id} className={index % 2 === 0 ? "bg-slate-150" : "bg-slate-100"}>
                                                    <TableCell>{product.id}</TableCell>
                                                    <TableCell className={index % 2 === 0 ? "bg-white sticky left-0 z-10 font-medium w-[150px]" : "bg-slate-100 sticky left-0 z-10 font-medium w-[100px]"}>
                                                        <a href={"/product/" + product.id}
                                                            className="group relative block overflow-hidden text-left"
                                                        >{product.longName}</a>

                                                    </TableCell>
                                                    <TableCell className="text-center text-nowrap">{product.salesUnit === 'A' && <p>Bucata</p>} {product.salesUnit === 'K' && <p>Kilogram</p>} {product.salesUnit === 'M' && <p>Metru</p>}</TableCell>
                                                    <TableCell className="text-center text-nowrap">{orderDetail.finalPrice} RON</TableCell>
                                                    <TableCell className="text-center text-nowrap">{orderDetail.orderedQuantity}</TableCell>
                                                    <TableCell className="text-center text-nowrap">{orderDetail.quantity}</TableCell>
                                                    <TableCell className="text-center text-nowrap"> {orderDetail.totalValueWithoutVAT} RON</TableCell>
                                                    <TableCell className="text-center text-nowrap"> {orderDetail.totalValue} RON</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </section>

                        <div className="text-center mt-8 mb-2">
                            <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                                <li>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (currentPage > 0) handlePageChange(currentPage - 1);
                                        }}
                                        className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                        aria-label="Previous page"
                                        disabled={currentPage === 0}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="size-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </li>

                                <li>
                                    <label htmlFor="Page" className="flex items-center gap-1">
                                        <span className="sr-only">Pagina</span>
                                        <input
                                            type="number"
                                            id="Page"
                                            value={inputValue}
                                            min={1}
                                            max={dataProducts.totalPages}
                                            onChange={handleInputChange}
                                            className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                        />
                                        <span className="text-sm">/ {dataProducts.totalPages}</span>
                                    </label>
                                </li>

                                <li>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (currentPage < dataProducts.totalPages - 1) handlePageChange(currentPage + 1);
                                        }}
                                        className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                        aria-label="Next page"
                                        disabled={currentPage === dataProducts.totalPages - 1}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="size-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Mobile Version */}
                <div className="relative mx-auto overflow-auto md:hidden ">
                    <div className="flow-root rounded-lg py-4 ml-2 mt-8 mb-8">
                        <dl className="flex flex-row flex-nowrap gap-3 -my-3 text-sm">
                            <div className="flex flex-col border w-5/6 border-gray-100">
                                <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
                                    <dt className="font-medium underline text-gray-900 ml-2 mr-12">Numar livrare</dt>
                                    <dd className="text-gray-700 sm:col-span-2 ml-2">{shippingId}</dd>
                                </div>

                                <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
                                    {/* <dt className="font-medium underline text-gray-900 ml-2">Comanda</dt>
                                    <dd className="text-gray-700 sm:col-span-2">
                                    </dd> */}
                                    <dt className="font-medium text-gray-900 ml-2">Numarul comenzii</dt>
                                    <dd className="text-gray-700 sm:col-span-2 ml-2">
                                        {data?.orderId}
                                    </dd>
                                    <dt className="font-medium text-gray-900 ml-2">Data integrarii comenzii</dt>
                                    <dd className="text-gray-700 sm:col-span-2 ml-2">
                                        {data?.orderIntegrationDate ? data?.orderIntegrationDate.split("T")[0] : '-'}
                                    </dd>
                                    <dt className="font-medium text-gray-900 ml-2">Data livrarii</dt>
                                    <dd className="text-gray-700 sm:col-span-2 ml-2">
                                        {data?.deliveryDate ? data?.deliveryDate.split("T")[0] : '-'}
                                    </dd>
                                </div>

                            </div>
                        </dl>
                    </div>

                    <div className="flex-initial w-7/8 overflow-auto ml-2 mb-2 shadow-md sm:w-4/5 sm:rounded-lg">

                        <section>
                            <div className="max-w-[1320px] m-auto px-4 mt-1 py-0 sm:px-6 sm:py-2 lg:px-8">
                                <Table>
                                    <TableCaption></TableCaption>
                                    <TableHeader>
                                        <TableRow className="bg-white border-slate-200 mb-1">
                                            <TableHead className="text-nowrap">Cod articol</TableHead>
                                            <TableHead className="sticky left-0 bg-white z-10 w-[100px] text-nowrap">Denumire
                                                articol</TableHead>
                                            <TableHead className="text-center">Unitate de vanzare</TableHead>
                                            {/* <TableHead className="text-center text-nowrap">Pret unitar/PCB/BAX <div className="text-blue-900">fara TVA</div></TableHead> */}
                                            <TableHead className="text-center text-nowrap">Pret pe bucata <div
                                                className="text-blue-900">fara TVA</div></TableHead>
                                            {/* <TableHead className="text-center text-nowrap">Pret/buc <div className="text-blue-900">TVA inclus</div></TableHead> */}
                                            <TableHead className="text-center">Cantitate comandata</TableHead>
                                            <TableHead className="text-center">Cantitate livrata</TableHead>
                                            <TableHead className="text-center">Total <div className="text-blue-900">fara TVA</div></TableHead>
                                            <TableHead className="text-center">Total <div className="text-blue-900">TVA inclus</div></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {dataProducts?.items.map((orderDetail: ShippingDetailModel, index: number) => {
                                            let product = orderDetail.article as ArticleModel;

                                            return (
                                                <TableRow key={product.id} className={index % 2 === 0 ? "bg-slate-150" : "bg-slate-100"}>
                                                    <TableCell>{product.id}</TableCell>
                                                    <TableCell className={index % 2 === 0 ? "bg-white sticky left-0 z-10 font-medium w-[150px]" : "bg-slate-100 sticky left-0 z-10 font-medium w-[100px]"}>
                                                        <a href={"/product/" + product.id}
                                                            className="group relative block overflow-hidden text-left"
                                                        >{product.longName}</a>

                                                    </TableCell>
                                                    <TableCell className="text-center text-nowrap">{product.salesUnit === 'A' && <p>Bucata</p>} {product.salesUnit === 'K' && <p>Kilogram</p>} {product.salesUnit === 'M' && <p>Metru</p>}</TableCell>
                                                    <TableCell className="text-center text-nowrap">{orderDetail.finalPrice} RON</TableCell>
                                                    <TableCell className="text-center text-nowrap">{orderDetail.orderedQuantity}</TableCell>
                                                    <TableCell className="text-center text-nowrap">{orderDetail.quantity}</TableCell>
                                                    <TableCell className="text-center text-nowrap"> {orderDetail.totalValueWithoutVAT} RON</TableCell>
                                                    <TableCell className="text-center text-nowrap"> {orderDetail.totalValue} RON</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </section>

                        <div className="text-center mt-8 mb-2">
                            <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                                <li>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (currentPage > 0) handlePageChange(currentPage - 1);
                                        }}
                                        className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                        aria-label="Previous page"
                                        disabled={currentPage === 0}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="size-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </li>

                                <li>
                                    <label htmlFor="Page" className="flex items-center gap-1">
                                        <span className="sr-only">Pagina</span>
                                        <input
                                            type="number"
                                            id="Page"
                                            value={inputValue}
                                            min={1}
                                            max={dataProducts.totalPages}
                                            onChange={handleInputChange}
                                            className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                        />
                                        <span className="text-sm">/ {dataProducts.totalPages}</span>
                                    </label>
                                </li>

                                <li>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (currentPage < dataProducts.totalPages - 1) handlePageChange(currentPage + 1);
                                        }}
                                        className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                        aria-label="Next page"
                                        disabled={currentPage === dataProducts.totalPages - 1}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="size-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default MyShippingDetails;